/**
 * @fileoverview Toast notification hook and state management.
 * Provides a global toast notification system using a reducer pattern
 * with in-memory state. Supports adding, updating, dismissing, and
 * removing toasts with automatic cleanup.
 * @module hooks/use-toast
 */

import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

/** Maximum number of toasts to display simultaneously */
const TOAST_LIMIT = 1

/** Delay before removing dismissed toasts from state (ms) */
const TOAST_REMOVE_DELAY = 1000000

/**
 * Extended toast type with additional display properties.
 */
type ToasterToast = ToastProps & {
  /** Unique toast identifier */
  id: string
  /** Toast title content */
  title?: React.ReactNode
  /** Toast description content */
  description?: React.ReactNode
  /** Optional action button element */
  action?: ToastActionElement
}

/** Available reducer action types */
const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

/** Toast ID counter for generating unique IDs */
let count = 0

/**
 * Generates a unique toast ID.
 * @returns Unique string ID
 */
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

/** Union type of all possible reducer actions */
type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

/** Toast state shape */
interface State {
  toasts: ToasterToast[]
}

/** Map of toast IDs to their removal timeout handles */
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * Queues a toast for removal after the configured delay.
 * @param toastId - ID of toast to remove
 */
const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

/**
 * Toast state reducer function.
 * Handles ADD, UPDATE, DISMISS, and REMOVE actions.
 * @param state - Current state
 * @param action - Action to apply
 * @returns New state
 */
export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

/** Array of state change listeners */
const listeners: Array<(state: State) => void> = []

/** In-memory toast state */
let memoryState: State = { toasts: [] }

/**
 * Dispatches an action and notifies all listeners.
 * @param action - Action to dispatch
 */
function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

/** Toast input type (without ID, which is auto-generated) */
type Toast = Omit<ToasterToast, "id">

/**
 * Creates and displays a new toast notification.
 * @param props - Toast properties (title, description, variant, etc.)
 * @returns Object with toast id, dismiss, and update methods
 */
function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

/**
 * Hook to access toast state and methods.
 * Subscribes to toast state changes and provides toast/dismiss functions.
 * @returns Object with toasts array, toast function, and dismiss function
 */
function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
